/**
 * The intake queue.
 *
 * The old File tab was one `<input type="file">`, one document, and a wait: pick a
 * file, fill in six fields, press File, then watch the upload happen. Anyone with a
 * pile of paper to get in did it once and stopped, which is the failure mode the
 * spec warns about — "an empty store teaches people the tool does not work".
 *
 * Three ideas fix that, and they are all in here rather than in the component:
 *
 *  1. **Uploading starts immediately.** The moment a file is chosen it begins
 *     transferring, while the person is still typing the title. By the time they
 *     press File the bytes are already in the bucket and filing is a metadata write.
 *  2. **Many at once.** A dropped folder of eight bills becomes eight rows, each
 *     independently editable and independently filed.
 *  3. **Guesses, offered not applied.** The filename usually contains the title and
 *     often the date. Vault fills those in and the person corrects them. It never
 *     files anything on the strength of a guess — see `infer`.
 */
import { api, ApiError } from "./api";

export type ItemStatus = "queued" | "hashing" | "uploading" | "ready" | "filing" | "filed" | "failed";

export type Guess = {
  title: string;
  docType: string;
  documentDate: string;
  /** Which fields came from the filename, so the UI can say so rather than imply certainty. */
  from: string[];
};

export type QueueItem = {
  /** Client-side only; the Firestore id arrives on filing. */
  key: string;
  filename: string;
  mime: string;
  sizeBytes: number;
  blob: Blob;
  origin: "picked" | "dropped" | "pasted" | "camera" | "drive";
  status: ItemStatus;
  /** 0–100. Real bytes-sent progress, not a spinner pretending. */
  progress: number;
  gcsPath: string | null;
  sha256: string | null;
  error: string | null;
  guess: Guess;
};

/**
 * How many files transfer at once.
 *
 * Four rather than "all of them": a dropped folder of thirty photographs on a home
 * ADSL line saturates the uplink, every transfer slows to a crawl, and the browser
 * starts timing out signed URLs that were issued fifteen minutes earlier. Four keeps
 * each one moving fast enough to finish inside its URL's life.
 */
const CONCURRENCY = 4;

let seq = 0;
export function nextKey(): string {
  seq += 1;
  return `q${seq}`;
}

// ── filename inference ─────────────────────────────────────────────────────

/**
 * Keyword → document type. Ordered most specific first, because "vehicle licence
 * renewal" contains both "licence" and "vehicle" and the vehicle reading is the
 * useful one. Deliberately a small, boring table: this is a suggestion a person
 * corrects in one click, and a cleverer classifier would only be wrong less
 * obviously.
 */
const TYPE_HINTS: Array<[RegExp, string]> = [
  [/\b(municipal|tshwane|joburg|city of|rates|utility|utilities)\b/i, "municipal_bill"],
  [/\b(passport|identity|\bid\b|id card|birth cert|marriage cert)\b/i, "identity"],
  [/\b(title deed|deed|erf|property|bond|levy|levies)\b/i, "property"],
  [/\b(licence disc|license disc|vehicle|car|registration|natis|rego)\b/i, "vehicle"],
  [/\b(insurance|policy|schedule|cover|premium|santam|outsurance|discovery insure)\b/i, "insurance"],
  [/\b(warranty|guarantee|receipt of purchase|appliance)\b/i, "warranty"],
  [/\b(medical|medical aid|discovery health|bonitas|momentum health|scrip|pathology|radiology)\b/i, "medical"],
  [/\b(vet|veterinary|dog|cat|rabies|deworm)\b/i, "vet"],
  [/\b(school|fees|report card|term|tuition|creche|kleuterskool)\b/i, "school"],
  [/\b(contract|agreement|lease|terms|nda)\b/i, "contract"],
  [/\b(receipt|invoice|slip|till|checkers|woolworths|pick n pay|takealot)\b/i, "receipt"],
];

/**
 * Pull an ISO date out of a filename.
 *
 * Only unambiguous forms. `2026-08-11` and `20260811` are certain; `11-08-2026` is
 * not — it is 11 August to a South African and 8 November to an American, and this
 * household's paper carries both conventions. A date silently read the wrong way
 * round produces an expiry nudge three months off, which is worse than no date at
 * all because it looks like it worked.
 */
function dateFromName(name: string): string {
  const iso = name.match(/(20\d{2})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  const compact = name.match(/\b(20\d{2})(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\b/);
  if (compact) return `${compact[1]}-${compact[2]}-${compact[3]}`;

  return "";
}

/** Tidy a filename into something a person would have typed. */
function titleFromName(name: string): string {
  let t = name
    .replace(/\.[A-Za-z0-9]{1,5}$/, "") // extension
    .replace(/[_+]+/g, " ")
    .replace(/\s*-\s*/g, " - ")
    // A date already shown in its own field is noise in the title.
    .replace(/\b20\d{2}-\d{2}-\d{2}\b/g, "")
    .replace(/\b20\d{6}\b/g, "")
    // Scanner and phone droppings: "Scan_0001", "IMG_20260811_094512", "(1)".
    .replace(/\b(scan|scanned|img|image|photo|doc|document|dsc|pxl)[\s-]*\d*\b/gi, "")
    .replace(/\(\d+\)/g, "")
    .replace(/\s{2,}/g, " ")
    .replace(/^[\s\-–—]+|[\s\-–—]+$/g, "")
    .trim();

  if (!t) return "";
  // Only capitalise a first letter that is lowercase — "eskom bill" becomes "Eskom
  // bill", while "SARS assessment" keeps its capitals.
  if (/^[a-z]/.test(t)) t = t[0].toUpperCase() + t.slice(1);
  return t.slice(0, 120);
}

/**
 * Guess title, type and date from a filename.
 *
 * `from` records which fields were guessed so the UI can label them as guesses. That
 * label is the whole reason this is honest rather than magic: a wrong title someone
 * can see was inferred gets corrected, and a wrong title that looks typed gets filed.
 */
export function infer(filename: string): Guess {
  const from: string[] = [];

  const title = titleFromName(filename);
  if (title) from.push("title");

  const documentDate = dateFromName(filename);
  if (documentDate) from.push("date");

  let docType = "generic";
  for (const [re, t] of TYPE_HINTS) {
    if (re.test(filename)) {
      docType = t;
      from.push("type");
      break;
    }
  }

  return { title, docType, documentDate, from };
}

// ── hashing ────────────────────────────────────────────────────────────────

/**
 * sha256 of the bytes, for the exact-duplicate check (item 29).
 *
 * Computed in the browser so the server can answer "you already have this" without
 * the file being uploaded twice. `crypto.subtle` needs a secure context; over plain
 * HTTP it is undefined, so this returns null and the duplicate check falls back to
 * the near-match on supplier/amount/date rather than throwing.
 */
/**
 * Fail loudly instead of hanging.
 *
 * Every step below — hashing, asking for a signed URL, the PUT itself — could
 * previously stall indefinitely with no timeout anywhere. The row simply sat at
 * "Reading…" and the banner said "Still uploading. Give it a moment." forever,
 * so a blocked upload and a slow one were indistinguishable. A stall that names
 * itself after 45 seconds is worth far more than a reassurance that never ends.
 */
async function withTimeout<T>(work: Promise<T>, ms: number, what: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const bell = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new ApiError(0, `${what} timed out after ${Math.round(ms / 1000)}s.`)), ms);
  });
  try {
    return await Promise.race([work, bell]);
  } finally {
    clearTimeout(timer!);
  }
}

export async function sha256(blob: Blob): Promise<string | null> {
  if (!crypto?.subtle) return null;
  try {
    const buf = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null;
  }
}

// ── upload ─────────────────────────────────────────────────────────────────

/**
 * PUT to a signed URL with real progress.
 *
 * XHR rather than `fetch`, for the one reason XHR still wins: `fetch` has no upload
 * progress event. A twelve-megabyte scan on a home connection takes long enough that
 * a bar which does not move reads as a hang, and people cancel and retry, which is
 * how you end up with the duplicates item 29 exists to clean up.
 */
export function put(
  url: string,
  blob: Blob,
  mime: string,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    // Generous, because a large scan on a home ADSL line is legitimately slow —
    // but finite, because a blocked or dead connection previously hung forever.
    xhr.timeout = 10 * 60_000;
    xhr.ontimeout = () =>
      reject(new ApiError(0, "The upload stalled and timed out. Check your connection and try again."));
    xhr.setRequestHeader("Content-Type", mime);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new ApiError(xhr.status, `The upload was refused (${xhr.status}).`));
    // A network-level failure gives no status and no body, so it needs its own
    // message — "refused (0)" tells nobody anything.
    xhr.onerror = () => reject(new ApiError(0, "The connection dropped during upload."));
    xhr.onabort = () => reject(new ApiError(0, "Upload cancelled."));
    xhr.send(blob);
  });
}

/**
 * Hash, then get a signed URL, then transfer. Mutates the item as it goes so the
 * component can render progress without threading callbacks through every layer.
 *
 * Never throws: a failure is recorded on the item as `status: "failed"` with a
 * readable reason. One unreadable file out of thirty must not take the other
 * twenty-nine down with it.
 */
export async function transfer(item: QueueItem, touch: () => void): Promise<void> {
  try {
    item.status = "hashing";
    item.progress = 0;
    touch();
    // Reading a local file should take milliseconds; 45s means something is
    // genuinely wrong rather than merely slow.
    item.sha256 = await withTimeout(sha256(item.blob), 45_000, "Reading the file");

    item.status = "uploading";
    touch();

    const up = await withTimeout(
      api.post<{ uploadUrl: string; gcsPath: string }>("/upload-url", {
        filename: item.filename,
        mime: item.mime,
      }),
      30_000,
      "Asking Vault for an upload link",
    );

    await put(up.uploadUrl, item.blob, item.mime, (pct) => {
      item.progress = pct;
      touch();
    });

    item.gcsPath = up.gcsPath;
    item.status = "ready";
    item.progress = 100;
  } catch (e) {
    item.status = "failed";
    item.error = e instanceof ApiError ? e.message : "Could not upload this file.";
  } finally {
    touch();
  }
}

/**
 * Run `transfer` over a list, `CONCURRENCY` at a time.
 *
 * A hand-rolled worker pool rather than chunked `Promise.all`: with chunks, one
 * 40 MB video in a batch of four holds up the next three files until it finishes.
 * Workers pull from a shared cursor, so a slow file only occupies its own slot.
 */
export async function transferAll(items: QueueItem[], touch: () => void): Promise<void> {
  let cursor = 0;
  const worker = async (): Promise<void> => {
    for (;;) {
      const i = cursor++;
      if (i >= items.length) return;
      await transfer(items[i], touch);
    }
  };
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, items.length) }, worker));
}

/** Bytes, for a person. */
export function size(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
